import AwsS3 from '@uppy/aws-s3';
import Uppy, { UppyFile } from '@uppy/core';
import { API_PATHS } from 'src/types/api';
import { config } from 'src/utils/config';
import { getBaseUrl, makeFetch } from 'src/utils/request';

type Abort = { signal: AbortSignal };
function createCauseError(error: string, cause: { message: string }) {
  return new Error(error, { cause: cause.message });
}
export function* serializeSubPart(
  key: string | null,
  value: string | Record<string, string | Record<string, string>>
): Generator<[string | null, string]> {
  if (typeof value !== 'object') {
    yield [key, value];
    return;
  }
  if (Array.isArray(value)) {
    for (const val of value) {
      yield* serializeSubPart(`${key}[]`, val);
    }
    return;
  }
  for (const [subkey, val] of Object.entries(value)) {
    yield* serializeSubPart(key ? `${key}[${subkey}]` : subkey, val);
  }
}
function serialize(data: Record<string, string | Record<string, string>>) {
  // If you want to avoid preflight requests, use URL-encoded syntax:
  // return new URLSearchParams(serializeSubPart(null, data));
  // If you don't care about additional preflight requests, you can also use:
  return JSON.stringify(data);
}

const MiB = 0x10_00_00;
const pluginId = 'widget-s3-plugin';
export function getUppyinstance(id = '') {
  const uppy = new Uppy({
    id: id,
    restrictions: {
      minNumberOfFiles: 1,
      maxFileSize: 10 * MiB,
      allowedFileTypes: ['.jpg', '.jpeg', '.png', '.gif', '.JPG', '.JPEG', '.PNG', '.GIF'],
    },
  });
  uppy.use(AwsS3, {
    id: pluginId,

    // Files that are more than 100MiB should be uploaded in multiple parts.
    shouldUseMultipart: (file) => (file?.size || 0) > 100 * MiB,

    /**
     * This method tells Uppy how to retrieve a temporary token for signing on the client.
     * Signing on the client is optional, you can also do the signing from the server.
     */
    async getTemporarySecurityCredentials(credentials) {
      const [response, error] = await makeFetch(`${getBaseUrl(API_PATHS.files)}/sts`, {
        signal: credentials?.signal,
      });
      if (error) throw createCauseError('Upload failed', error);
      return response;
    },

    // ========== Non-Multipart Uploads ==========

    /**
     * This method tells Uppy how to handle non-multipart uploads.
     * If for some reason you want to only support multipart uploads,
     * you don't need to implement it.
     */
    // @ts-expect-error functions are extensible
    async getUploadParameters(file: UppyFile, options: Abort & { key?: string }) {
      // if (typeof crypto?.subtle === 'object') {
      //   // If WebCrypto is available, let's do signing from the client.
      //   options.key = file.meta.key;
      //   // @ts-expect-error type not inferred correctly
      //   return uppy.getPlugin(pluginId).createSignedURL(file, options);
      // }
      // Send a request to our Express.js signing endpoint.
      if (typeof file.meta.key !== 'string') {
        throw new Error('File key is not a string');
      }
      const [data, error] = await makeFetch(`${getBaseUrl(API_PATHS.files)}/sign-s3`, {
        method: 'POST',
        headers: {
          accept: 'application/json',
        },
        body: serialize({
          filename: file.meta.key.replace(`private/photos/${config.value.clientId}/`, ''),
          contentType: String(file.type),
          folder: `private/photos/${config.value.clientId}/`,
        }),
        signal: options.signal,
      });
      if (error) throw createCauseError('Upload Failed', error);

      // Return an object in the correct shape.
      return {
        method: data.method,
        url: data.url,
        fields: {}, // For presigned PUT uploads, this should be left empty.
        // Provide content type header required by S3
        headers: {
          'Content-Type': file.type,
          'Content-Disposition': `attachment; filename*=UTF-8''${encodeURIComponent(file.name)}`,
        },
      };
    },

    // ========== Multipart Uploads ==========

    // The following methods are only useful for multipart uploads:
    // If you are not interested in multipart uploads, you don't need to
    // implement them (you'd also need to set `shouldUseMultipart: false` though).
    // @ts-expect-error functions are extensible
    async createMultipartUpload(file: UppyFile, signal: AbortSignal) {
      if (signal?.aborted) {
        const err = new DOMException('The operation was aborted', 'AbortError');
        Object.defineProperty(err, 'cause', {
          configurable: true,
          writable: true,
          value: signal.reason,
        });
        throw err;
      }

      const metadata: Record<string, string> = {};

      Object.keys(file.meta || {}).forEach((key) => {
        if (file.meta[key] != null) {
          metadata[key] = String(file.meta[key]);
        }
      });

      const [data, error] = await makeFetch(`${getBaseUrl(API_PATHS.files)}/s3/multipart`, {
        method: 'POST',
        // Send and receive JSON.
        headers: {
          accept: 'application/json',
        },
        body: serialize({
          filename: file.name,
          type: String(file.type),
          metadata: metadata,
        }),
        signal,
      });

      if (error) throw createCauseError('Unsuccessful request', error);

      return data;
    },
    // @ts-expect-error functions are extensible
    async abortMultipartUpload(file, { key, uploadId }, signal) {
      const filename = encodeURIComponent(key);
      const uploadIdEnc = encodeURIComponent(uploadId);
      const [, error] = await makeFetch(`${getBaseUrl(API_PATHS.files)}/s3/multipart/${uploadIdEnc}?key=${filename}`, {
        method: 'DELETE',
        signal,
      });

      if (error) throw createCauseError('Unsuccessful request', error);
    },

    async signPart(file, options) {
      if (typeof crypto?.subtle === 'object') {
        // If WebCrypto, let's do signing from the client.
        // @ts-expect-error functions are extensible
        return uppy.getPlugin(pluginId).createSignedURL(file, options);
      }

      const { uploadId, key, partNumber, signal } = options;

      if (signal?.aborted) {
        const err = new DOMException('The operation was aborted', 'AbortError');
        Object.defineProperty(err, 'cause', {
          configurable: true,
          writable: true,
          value: signal.reason,
        });
        throw err;
      }

      if (uploadId == null || key == null || partNumber == null) {
        throw new Error('Cannot sign without a key, an uploadId, and a partNumber');
      }

      const filename = encodeURIComponent(key);
      const [data, error] = await makeFetch(
        `${getBaseUrl(API_PATHS.files)}/s3/multipart/${uploadId}/${partNumber}?key=${filename}`,
        { signal }
      );

      if (error) throw createCauseError('Unsuccessful request', error);

      return data;
    },
    // @ts-expect-error functions are extensible
    async listParts(file, { key, uploadId }, signal) {
      if (signal?.aborted) {
        const err = new DOMException('The operation was aborted', 'AbortError');
        Object.defineProperty(err, 'cause', {
          configurable: true,
          writable: true,
          value: signal.reason,
        });
        throw err;
      }

      const filename = encodeURIComponent(key);
      const [data, error] = await makeFetch(`${getBaseUrl(API_PATHS.files)}/s3/multipart/${uploadId}?key=${filename}`, {
        signal,
      });

      if (error) throw createCauseError('Unsuccessful request', error);

      return data;
    },
    // @ts-expect-error functions are extensible
    async completeMultipartUpload(file, { key, uploadId, parts }, signal) {
      if (signal?.aborted) {
        const err = new DOMException('The operation was aborted', 'AbortError');
        Object.defineProperty(err, 'cause', {
          configurable: true,
          writable: true,
          value: signal.reason,
        });
        throw err;
      }

      const filename = encodeURIComponent(key);
      const uploadIdEnc = encodeURIComponent(uploadId);
      const [data, error] = await makeFetch(
        `${getBaseUrl(API_PATHS.files)}s3/multipart/${uploadIdEnc}/complete?key=${filename}`,
        {
          method: 'POST',
          headers: {
            accept: 'application/json',
          },
          body: serialize({ parts }),
          signal,
        }
      );

      if (error) throw createCauseError('Unsuccessful request', error);

      return data;
    },
  });
  return uppy;
}

export async function getSignedUrl(key: string) {
  const [data, error] = await makeFetch(`${getBaseUrl(API_PATHS.files)}/sign-s3/${encodeURIComponent(key)}`);
  if (error) throw error;

  return String(data);
}
